// External packages
import * as React from 'react';
import { z } from 'zod';

// Components
import { FileInputField } from 'components/FileInput';
import { Button } from 'components/Button';
import * as Modal from 'components/Modal';
import { Form } from 'components/Form';
import { Tab, TabList, TabPanel, Tabs } from 'components/Tabs';
import { TiptapEditorField } from 'components/TiptapEditor';
import { Label } from 'components/Forms';
import { SelectMultiple } from 'components/SelectMultiple';
import { Option } from 'components/Select';

// Modules
import {
  AddValue,
  Language,
  TranslationsCleaner,
} from 'modules/exhibits/ExhibitsModal';

// Data
import languagesData from 'data/languages.json';

const imageFormSchema = z.object({
  image: z.instanceof(File).refine((file) => file instanceof File, {
    message: 'Unesite sliku',
  }),
  translations: z.array(
    z.object({
      languageCode: z.string(),
      description: z.string({ message: 'Dodajte opis' }),
    })
  ),
});

type AddImageModalProps = {
  onSubmit: (data: z.infer<typeof imageFormSchema>) => Promise<void> | void;
  modalTrigger?: React.ReactNode;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  isPending?: boolean;
};

export const AddImageModal: React.FC<AddImageModalProps> = ({
  onSubmit,
  modalTrigger,
  isOpen,
  onOpenChange,
  isPending = false,
}) => {
  const languages: Language[] = languagesData;

  const [selectedLanguages, setSelectedLanguages] = React.useState<Language[]>([
    languages[0],
  ]);

  const handleLanguageChange = (keys: unknown) => {
    let arr: string[] = Array.isArray(keys)
      ? keys.map(String)
      : keys instanceof Set
        ? Array.from(keys).map(String)
        : keys
          ? [String(keys)]
          : [];
    arr = Array.from(new Set(arr));

    if (arr.length === 0) {
      return;
    }

    const langs = arr
      .map((code) => languages.find((l) => l.langEnglishName === code))
      .filter(Boolean) as Language[];

    setSelectedLanguages(langs);
  };

  return (
    <Modal.TriggerRoot isOpen={isOpen} onOpenChange={onOpenChange}>
      {modalTrigger}
      <Modal.Overlay isDismissable>
        <Modal.Root>
          <Modal.Dialog>
            <Modal.Close />
            <h3 className="mb-8 text-2xl text-gray-600">Dodaj sliku</h3>
            <Form schema={imageFormSchema} onSubmit={onSubmit}>
              <FileInputField
                fileTriggerProps={{
                  acceptedFileTypes: [
                    'image/jpeg',
                    'image/png',
                    'image/gif',
                    'image/webp',
                    'image/svg+xml',
                  ],
                }}
                label="Slika"
                name="image"
                wrapperClassName="mb-4"
              />
              <TranslationsCleaner selectedLanguages={selectedLanguages} />
              <div className="mb-4">
                <Label>Odaberi jezike</Label>
                <SelectMultiple
                  selectedKeys={selectedLanguages.map((l) => l.langEnglishName)}
                  onSelectionChange={handleLanguageChange}
                >
                  {languages &&
                    languages.map((lang) => (
                      <Option key={lang.langCode} id={lang.langEnglishName}>
                        {lang.langNativeName} ({lang.langEnglishName})
                      </Option>
                    ))}
                </SelectMultiple>
              </div>
              <Tabs className="mb-4">
                <TabList className="!justify-start">
                  {selectedLanguages.map((lang) => (
                    <Tab key={lang.langCode} id={lang.langEnglishName}>
                      {lang.langEnglishName}
                    </Tab>
                  ))}
                </TabList>
                {selectedLanguages.map((lang) => {
                  const index = selectedLanguages.findIndex(
                    (l) => l.langCode === lang.langCode
                  );

                  return (
                    <TabPanel
                      key={lang.langCode}
                      id={lang.langEnglishName}
                      className="px-4 py-6"
                    >
                      <AddValue index={index} langCode={lang.langCode} />
                      <TiptapEditorField
                        isRequired
                        name={`translations.${index}.description`}
                        editorProps={{ colorScheme: 'white' }}
                        label={`Opis (${lang.langEnglishName})`}
                        className="flex-1 mb-4"
                      />
                    </TabPanel>
                  );
                })}
              </Tabs>
              <Button
                type="submit"
                className="w-full mt-12"
                size="lg"
                isPending={isPending}
              >
                Dodaj sliku
              </Button>
            </Form>
          </Modal.Dialog>
        </Modal.Root>
      </Modal.Overlay>
    </Modal.TriggerRoot>
  );
};
